import React, { useState, useEffect } from 'react';
import { content, colors } from '../data/content';

const Header = ({ language, onLanguageChange, isScrolled }) => {
  const currentContent = content[language].nav;
  const isRTL = language === 'ar';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track active section on scroll using Intersection Observer
  useEffect(() => {
    const sections = ['hero', 'about', 'whereToBuy', 'wholesale'];
    
    // Track which sections are currently visible
    const visibleSections = new Set();
    
    const updateActiveSection = () => {
      const scrollY = window.scrollY;
      let activeId = 'hero';
      
      // If at the very top, always show hero
      if (scrollY < 100) {
        setActiveSection('hero');
        return;
      }

      // If we have visible sections, use the first one from top to bottom
      if (visibleSections.size > 0) {
        for (let i = 0; i < sections.length; i++) {
          if (visibleSections.has(sections[i])) {
            activeId = sections[i];
            break;
          }
        }
      } else {
        // No visible sections - find the section we're currently in based on scroll position
        // Check from top to bottom, not bottom to top
        for (let i = 0; i < sections.length; i++) {
          const section = document.getElementById(sections[i]);
          if (!section) continue;

          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          // Check if scroll position is within this section's bounds
          // Use a smaller offset to be more precise
          if (scrollY + 150 >= sectionTop && scrollY + 150 < sectionBottom) {
            activeId = sections[i];
            break;
          }
          
          // Special handling for last section (wholesale)
          // Only activate if we're actually past its start AND within its bounds
          if (i === sections.length - 1) {
            if (scrollY + 150 >= sectionTop && scrollY < sectionBottom) {
              activeId = sections[i];
            }
          }
        }
      }

      setActiveSection(activeId);
    };

    const observerOptions = {
      root: null,
      rootMargin: '-25% 0px -55% 0px', // Trigger when section is in upper 45% of viewport
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5]
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        // Only consider section visible if it has significant intersection
        if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
          visibleSections.add(entry.target.id);
        } else {
          visibleSections.delete(entry.target.id);
        }
      });
      updateActiveSection();
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    // Wait for DOM to be ready, then observe all sections
    const timeoutId = setTimeout(() => {
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) {
          observer.observe(section);
        }
      });
      
      // Initial check
      updateActiveSection();
    }, 200);

    // Also listen to scroll for more accurate tracking
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  const menuItems = [
    { id: 'hero', label: currentContent.menu.home || (isRTL ? 'الرئيسية' : 'Home') },
    { id: 'about', label: currentContent.menu.about || (isRTL ? 'من نحن' : 'About') },
    { id: 'whereToBuy', label: currentContent.menu.where || (isRTL ? 'أين تجدنا' : 'Where to Buy') },
    { id: 'wholesale', label: currentContent.menu.wholesale || (isRTL ? 'الجملة' : 'Wholesale') },
  ];

  const handleMenuClick = (sectionId) => {
    setIsMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 90;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: isScrolled 
          ? 'rgba(255, 255, 255, 0.98)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: isScrolled 
          ? '0 4px 20px rgba(0, 0, 0, 0.08)' 
          : '0 2px 10px rgba(0, 0, 0, 0.05)',
        padding: isScrolled ? '12px 0' : '16px 0',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        borderBottom: isScrolled ? `1px solid rgba(38, 60, 125, 0.1)` : 'none'
      }}
    >
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: `0 clamp(20px, 4vw, 32px)`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* Logo */}
        <a 
          href="#hero" 
          onClick={(e) => {
            e.preventDefault();
            handleMenuClick('hero');
          }}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '14px',
            textDecoration: 'none',
            transition: 'transform 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <div style={{
            width: isMobile ? '44px' : (isScrolled ? '50px' : '60px'),
            height: isMobile ? '44px' : (isScrolled ? '50px' : '60px'),
            borderRadius: '12px',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <img 
              src="/tunagwaizi-uae Logo.png" 
              alt="Tuna Gwaizi UAE Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                padding: '4px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          {!isMobile && (
            <span style={{ 
              fontWeight: 700, 
              fontSize: isScrolled ? '18px' : '20px',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: language === 'ar' ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
              transition: 'all 0.3s ease',
              letterSpacing: language === 'ar' ? '0' : '0.5px',
              whiteSpace: 'nowrap'
            }}>
              {currentContent.logo}
            </span>
          )}
        </a>
        
        {/* Desktop Menu */}
        {!isMobile && (
          <div 
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              background: 'rgba(38, 60, 125, 0.05)',
              padding: '6px',
              borderRadius: '16px',
              backdropFilter: 'blur(10px)'
            }}
          >
            {menuItems.map((item, index) => {
              const isActive = activeSection === item.id;
              return (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick(item.id);
                  }}
                  style={{
                    color: isActive ? colors.primary : colors.secondary,
                    textDecoration: 'none',
                    fontSize: '15px',
                    fontWeight: isActive ? 600 : 500,
                    padding: '10px 20px',
                    borderRadius: '12px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                    background: isActive ? 'white' : 'transparent',
                    boxShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    position: 'relative',
                    animation: `fadeInUp 0.5s ease ${index * 0.1}s both`
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                      e.currentTarget.style.color = colors.primary;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = colors.secondary;
                      e.currentTarget.style.transform = 'none';
                    }
                  }}
                >
                  {item.label}
                </a>
              );
            })}
          </div>
        )}

        {/* Right Side - Language Switcher & Mobile Menu */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Language Switcher */}
          <div style={{
            display: 'flex',
            gap: '4px',
            background: 'rgba(38, 60, 125, 0.08)',
            borderRadius: '12px',
            padding: '4px',
            backdropFilter: 'blur(10px)'
          }}>
            <button
              onClick={() => onLanguageChange('en')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                background: language === 'en' 
                  ? `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)` 
                  : 'transparent',
                color: language === 'en' ? 'white' : colors.secondary,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: 'Poppins, sans-serif',
                boxShadow: language === 'en' ? '0 2px 8px rgba(38, 60, 125, 0.3)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (language !== 'en') {
                  e.currentTarget.style.background = 'rgba(38, 60, 125, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (language !== 'en') {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {currentContent.enButton}
            </button>
            <button
              onClick={() => onLanguageChange('ar')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                background: language === 'ar' 
                  ? `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)` 
                  : 'transparent',
                color: language === 'ar' ? 'white' : colors.secondary,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: 'Cairo, sans-serif',
                boxShadow: language === 'ar' ? '0 2px 8px rgba(38, 60, 125, 0.3)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (language !== 'ar') {
                  e.currentTarget.style.background = 'rgba(38, 60, 125, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (language !== 'ar') {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {currentContent.arButton}
            </button>
          </div>

          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '10px',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(38, 60, 125, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{
                width: '24px',
                height: '3px',
                background: colors.secondary,
                borderRadius: '2px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isMenuOpen ? 'rotate(45deg) translate(6px, 6px)' : 'none'
              }}></span>
              <span style={{
                width: '24px',
                height: '3px',
                background: colors.secondary,
                borderRadius: '2px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: isMenuOpen ? 0 : 1,
                transform: isMenuOpen ? 'translateX(-20px)' : 'none'
              }}></span>
              <span style={{
                width: '24px',
                height: '3px',
                background: colors.secondary,
                borderRadius: '2px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isMenuOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none'
              }}></span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobile && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            padding: '20px',
            display: isMenuOpen ? 'flex' : 'none',
            flexDirection: 'column',
            gap: '8px',
            animation: isMenuOpen ? 'fadeInUp 0.3s ease' : 'none',
            borderTop: `1px solid rgba(38, 60, 125, 0.1)`
          }}
        >
          {menuItems.map((item, index) => {
            const isActive = activeSection === item.id;
            return (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuClick(item.id);
                }}
                style={{
                  color: isActive ? colors.primary : colors.secondary,
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: isActive ? 600 : 500,
                  padding: '14px 20px',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  background: isActive ? 'rgba(191, 10, 29, 0.1)' : 'transparent',
                  borderLeft: isRTL ? 'none' : isActive ? `3px solid ${colors.primary}` : 'none',
                  borderRight: isRTL ? isActive ? `3px solid ${colors.primary}` : 'none' : 'none',
                  animation: `fadeInUp 0.3s ease ${index * 0.1}s both`
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(38, 60, 125, 0.05)';
                    e.currentTarget.style.color = colors.primary;
                    e.currentTarget.style.transform = 'translateX(8px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = colors.secondary;
                    e.currentTarget.style.transform = 'none';
                  }
                }}
              >
                {item.label}
              </a>
            );
          })}
        </div>
      )}
    </nav>
  );
};

export default Header;
